import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  LocalShipping as ShippingIcon,
  DirectionsCar as CarIcon,
  People as PeopleIcon,
  Map as MapIcon,
  Business as BusinessIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';

const drawerWidth = 240;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const getMenuItems = () => {
    if (user?.role === 'customer') {
      return [
        { text: 'Dashboard', path: '/customer/dashboard', icon: <DashboardIcon /> },
        { text: 'Quotes', path: '/customer/quotes', icon: <AssignmentIcon /> },
        { text: 'Orders', path: '/customer/orders', icon: <ShippingIcon /> },
        { text: 'Invoices', path: '/customer/invoices', icon: <ReceiptIcon /> },
        { text: 'Payment Methods', path: '/customer/payment-methods', icon: <CreditCardIcon /> },
        { text: 'Profile', path: '/customer/profile', icon: <PersonIcon /> },
      ];
    } else if (user?.role === 'driver') {
      return [
        { text: 'Dashboard', path: '/driver/dashboard', icon: <DashboardIcon /> },
        { text: 'Jobs', path: '/driver/jobs', icon: <AssignmentIcon /> },
        { text: 'Map', path: '/driver/map', icon: <MapIcon /> },
        { text: 'Profile', path: '/driver/profile', icon: <PersonIcon /> },
      ];
    } else if (user?.role === 'manager') {
      return [
        { text: 'Dashboard', path: '/manager/dashboard', icon: <DashboardIcon /> },
        { text: 'Drivers', path: '/manager/drivers', icon: <PersonIcon /> },
        { text: 'Customers', path: '/manager/customers', icon: <PeopleIcon /> },
        { text: 'Jobs', path: '/manager/jobs', icon: <AssignmentIcon /> },
        { text: 'Invoices', path: '/manager/invoices', icon: <ReceiptIcon /> },
        { text: 'Pricing', path: '/manager/pricing', icon: <ReceiptIcon /> },
        { text: 'Vehicles', path: '/manager/vehicles', icon: <CarIcon /> },
        { text: 'Map', path: '/manager/map', icon: <MapIcon /> },
        { text: 'Organization', path: '/manager/organization', icon: <BusinessIcon /> },
        { text: 'Profile', path: '/manager/profile', icon: <PersonIcon /> },
      ];
    }
    return [];
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box>
      <Toolbar />
      <Box sx={{ overflow: 'auto' }}>
        <List>
          {getMenuItems().map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  '&.Mui-selected': {
                    background: 'rgba(30, 58, 95, 0.08)',
                    borderLeft: '3px solid #1E3A5F',
                    '&:hover': {
                      background: 'rgba(30, 58, 95, 0.12)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#1E3A5F',
                    },
                    '& .MuiListItemText-primary': {
                      color: '#1E3A5F',
                      fontWeight: 600,
                    },
                  },
                  '&:hover': {
                    background: 'rgba(30, 58, 95, 0.05)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? '#1E3A5F' : '#666',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiTypography-root': {
                      color: location.pathname === item.path ? '#1E3A5F' : '#333',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + (isMobile ? 0 : 1),
          background: '#1E3A5F',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Logo size="small" variant="icon" />
            <Typography
              variant={isMobile ? 'subtitle1' : 'h6'}
              noWrap
              component="div"
              sx={{
                ml: 2,
                fontWeight: 600,
                color: '#ffffff',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              DND Diesel and Def Delivery
            </Typography>
            <Typography
              variant="subtitle2"
              noWrap
              component="div"
              sx={{
                ml: 2,
                fontWeight: 600,
                color: '#ffffff',
                display: { xs: 'block', sm: 'none' },
              }}
            >
              DND Delivery
            </Typography>
          </Box>
          {!isMobile && (
            <Typography
              variant="body2"
              sx={{
                mr: 2,
                color: 'rgba(255, 255, 255, 0.9)',
                display: { xs: 'none', md: 'block' },
              }}
            >
              {user?.email}
            </Typography>
          )}
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              color: '#ffffff',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)',
              },
              '& .MuiButton-startIcon': {
                marginRight: { xs: 0, sm: 1 },
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Logout
            </Box>
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="navigation"
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                background: '#ffffff',
                borderRight: '1px solid rgba(30, 58, 95, 0.1)',
              },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                background: '#ffffff',
                borderRight: '1px solid rgba(30, 58, 95, 0.1)',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: '100vh',
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;



